import React, { useState, useEffect, useRef, useMemo, useContext, useCallback } from 'react';
import { 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  ChevronDown, 
  Filter, 
  Settings, 
  Download, 
  Check, 
  Eye, 
  ArrowUp, 
  ArrowDown, 
  Calendar, 
  DollarSign,
  CheckSquare,
  ChevronsRight,
  ExternalLink,
  Mail,
  MoreHorizontal,
  ToggleLeft
} from 'lucide-react';
import Dashboard, { useDashboard } from '../../Layouts/Dashboard';
import TableHeader from '../../Components/TableHeader';
import TablePagination from '../../Components/TablePagination';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import { HttpRequestContext } from '../../Blocks/HttpRequestProvider';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowDropDown as ArrowDropDownIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  GetApp as GetAppIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

// Define breadcrumbs for this page
const factureListBreadcrumbs = [
  { href: '/', label: 'Accueil' },
  { href: '/invoicing', label: 'Facturation' },
  { label: 'Mes Factures' }
];

// Storage keys for persistence
const STORAGE_KEY_PREFIX = 'factureList_';
const STORAGE_KEYS = {
  COLUMNS: `${STORAGE_KEY_PREFIX}columns`,
  PAGE_SIZE: `${STORAGE_KEY_PREFIX}pageSize`,
  SORT_CONFIG: `${STORAGE_KEY_PREFIX}sortConfig`,
  FILTER_STATUS: `${STORAGE_KEY_PREFIX}filterStatus`
};

// Default columns for the invoice list
const DEFAULT_COLUMNS = [
  { id: 'number', name: 'Numéro', visible: true, sortable: true },
  { id: 'client', name: 'Client', visible: true, sortable: true },
  { id: 'emission_date', name: 'Date d\'émission', visible: true, sortable: true },
  { id: 'due_date', name: 'Date d\'échéance', visible: true, sortable: true },
  { id: 'status', name: 'Statut', visible: true, sortable: true },
  { id: 'amount', name: 'Montant', visible: true, sortable: true },
  { id: 'amount_htva', name: 'Montant HTVA', visible: false, sortable: true },
  { id: 'amount_tvac', name: 'Montant TVAC', visible: false, sortable: true },
  { id: 'currency', name: 'Devise', visible: false, sortable: true },
  { id: 'actions', name: 'Actions', visible: true, sortable: false, align: 'right' }
];

// Default page size
const DEFAULT_PAGE_SIZE = 10;

// Default sort configuration
const DEFAULT_SORT_CONFIG = { key: 'emission_date', direction: 'desc' };

// Status options for filtering
const STATUS_OPTIONS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'Brouillon', label: 'Brouillon' },
  { value: 'Envoyée', label: 'Envoyée' },
  { value: 'Payée', label: 'Payée' },
  { value: 'Annulée', label: 'Annulée' }
];

// Helper to safely parse JSON from localStorage with fallback
const getSavedValue = (key, defaultValue) => {
  try {
    const savedValue = localStorage.getItem(key);
    return savedValue !== null ? JSON.parse(savedValue) : defaultValue;
  } catch (error) {
    console.error(`Error loading saved setting for ${key}:`, error);
    return defaultValue;
  }
};

// Validate saved columns
const validateSavedColumns = (savedColumns) => {
  if (!savedColumns || !Array.isArray(savedColumns) || savedColumns.length === 0) {
    return DEFAULT_COLUMNS;
  }

  // Check if we need to migrate old column format with separate HTVA/TVAC
  const hasOldAmountColumns = savedColumns.some(col => 
    col.id === 'amount_htva' || col.id === 'amount_tvac'
  );
  const hasNewAmountColumn = savedColumns.some(col => col.id === 'amount');
  
  if (hasOldAmountColumns && !hasNewAmountColumn) {
    // Add new amount column
    const newColumns = [...savedColumns];
    
    // Find position of HTVA column to insert the new amount column
    const htvaIndex = newColumns.findIndex(col => col.id === 'amount_htva');
    if (htvaIndex !== -1) {
      // Insert at HTVA position
      newColumns.splice(htvaIndex, 0, { 
        id: 'amount', 
        name: 'Montant', 
        visible: true, 
        sortable: true 
      });
      
      // Hide the old columns
      const htvaCol = newColumns.find(col => col.id === 'amount_htva');
      const tvacCol = newColumns.find(col => col.id === 'amount_tvac');
      
      if (htvaCol) htvaCol.visible = false;
      if (tvacCol) tvacCol.visible = false;
    }
    
    return newColumns;
  }

  const validColumnIds = new Set(DEFAULT_COLUMNS.map(col => col.id));
  const allColumnsValid = savedColumns.every(col => 
    col && typeof col === 'object' && 
    validColumnIds.has(col.id) &&
    'visible' in col && 'sortable' in col && 'name' in col
  );

  if (!allColumnsValid || savedColumns.length !== DEFAULT_COLUMNS.length) {
    console.warn('Invalid saved columns detected, using defaults');
    return DEFAULT_COLUMNS;
  }

  return savedColumns;
};

// Validate sort configuration
const validateSortConfig = (sortConfig, columns) => {
  if (!sortConfig || typeof sortConfig !== 'object') {
    return DEFAULT_SORT_CONFIG;
  }

  const validSortableColumnIds = columns
    .filter(col => col.sortable)
    .map(col => col.id);
  
  const isValidSortKey = sortConfig.key === null || 
    (validSortableColumnIds.includes(sortConfig.key));
  
  const isValidDirection = sortConfig.direction === 'asc' || sortConfig.direction === 'desc';

  if (!isValidSortKey || !isValidDirection) {
    console.warn('Invalid sort configuration detected, using defaults');
    return DEFAULT_SORT_CONFIG;
  }

  return sortConfig;
};

// Format currency as EUR
const formatCurrency = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

// Format date for display
const formatDate = (dateString) => {
  try {
    if (!dateString) return '';
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    return format(date, 'dd MMM yyyy', { locale: fr });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString || '';
  }
};

// Get client name from client object
const getClientName = (client) => {
  if (!client) return 'Client inconnu';
  
  // If there's a company name, use it
  if (client.entity_name) {
    return client.entity_name;
  }
  
  // Otherwise use first and last name
  const firstName = client.firstname || '';
  const lastName = client.lastname || '';
  return `${firstName} ${lastName}`.trim() || 'Client inconnu';
};

// Get status color based on status value
const getStatusColor = (status) => {
  switch (status) {
    case 'Brouillon':
      return { color: 'info', label: 'Brouillon', textColor: 'text-blue-600', bgColor: 'bg-blue-100' };
    case 'Envoyée':
      return { color: 'warning', label: 'Envoyée', textColor: 'text-orange-600', bgColor: 'bg-orange-100' };
    case 'Payée':
      return { color: 'success', label: 'Payée', textColor: 'text-green-600', bgColor: 'bg-green-100' };
    case 'Annulée':
      return { color: 'error', label: 'Annulée', textColor: 'text-red-600', bgColor: 'bg-red-100' };
    default:
      return { color: 'default', label: status, textColor: 'text-gray-600', bgColor: 'bg-gray-100' };
  }
};

const FactureListContent = ({ setHeaderActions: propSetHeaderActions }) => {
  // Get setHeaderActions from context
  const { setHeaderActions: contextSetHeaderActions } = useDashboard();
  
  // Use the prop version if provided (backward compatibility) or the context version
  const setHeaderActions = propSetHeaderActions || contextSetHeaderActions;

  const { sendRequest } = useContext(HttpRequestContext);
  const navigate = useNavigate();
  
  // State for invoices data
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => 
    getSavedValue(STORAGE_KEYS.PAGE_SIZE, DEFAULT_PAGE_SIZE)
  );
  
  // State for sorting
  const [sortConfig, setSortConfig] = useState(() => {
    const savedSortConfig = getSavedValue(STORAGE_KEYS.SORT_CONFIG, DEFAULT_SORT_CONFIG);
    return validateSortConfig(savedSortConfig, DEFAULT_COLUMNS);
  });
  
  // State for filtering
  const [filterStatus, setFilterStatus] = useState(() => 
    getSavedValue(STORAGE_KEYS.FILTER_STATUS, 'all')
  );
  
  // State for search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // State for columns display
  const [columns, setColumns] = useState(() => {
    const savedColumns = getSavedValue(STORAGE_KEYS.COLUMNS, DEFAULT_COLUMNS);
    return validateSavedColumns(savedColumns);
  });
  
  // State for UI controls
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  
  // Refs for UI elements
  const columnSettingsRef = useRef(null);
  const columnSettingsBtnRef = useRef(null);
  
  // Fetch invoices from the API
  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await sendRequest({
        apiPath: 'invoicing/facture/all',
        type: 'GET'
      });
      
      if (response && response.invoices) {
        setInvoices(response.invoices);
        setTotalItems(response.invoices.length);
      } else {
        console.error('Failed to fetch invoices:', response);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setIsLoading(false);
    }
  }, [sendRequest]);
  
  // Initialize by fetching invoices
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);
  
  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.COLUMNS, JSON.stringify(columns));
  }, [columns]);
  
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.PAGE_SIZE, JSON.stringify(pageSize));
  }, [pageSize]);
  
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SORT_CONFIG, JSON.stringify(sortConfig));
  }, [sortConfig]);
  
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.FILTER_STATUS, JSON.stringify(filterStatus));
  }, [filterStatus]);
  
  // Filter and sort invoices
  const filteredAndSortedInvoices = useMemo(() => {
    let filteredList = [...invoices];
    
    // Apply status filter
    if (filterStatus !== 'all') {
      filteredList = filteredList.filter(invoice => invoice.status === filterStatus);
    }
    
    // Apply search term filter
    if (searchTerm.trim()) {
      filteredList = filteredList.filter(invoice => {
        const searchLower = searchTerm.toLowerCase();
        const clientName = getClientName(invoice.client).toLowerCase();
        
        return (
          invoice.num_document?.toLowerCase().includes(searchLower) ||
          clientName.includes(searchLower)
        );
      });
    }
    
    // Apply sorting
    if (sortConfig.key) {
      filteredList.sort((a, b) => {
        let aValue, bValue;
        
        switch (sortConfig.key) {
          case 'number':
            aValue = a.num_document || '';
            bValue = b.num_document || '';
            break;
          case 'client':
            aValue = getClientName(a.client);
            bValue = getClientName(b.client);
            break;
          case 'emission_date':
            aValue = a.date_emission ? new Date(a.date_emission) : new Date(0);
            bValue = b.date_emission ? new Date(b.date_emission) : new Date(0);
            break;
          case 'due_date':
            aValue = a.date_echeance ? new Date(a.date_echeance) : new Date(0);
            bValue = b.date_echeance ? new Date(b.date_echeance) : new Date(0);
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          case 'amount':
            // Sort by TVAC by default
            aValue = a.total_tvac || 0;
            bValue = b.total_tvac || 0;
            break;
          case 'amount_htva': // Keep for backward compatibility
            aValue = a.total_htva || 0;
            bValue = b.total_htva || 0;
            break;
          case 'amount_tvac': // Keep for backward compatibility
            aValue = a.total_tvac || 0;
            bValue = b.total_tvac || 0;
            break;
          case 'currency':
            aValue = a.currency || '';
            bValue = b.currency || '';
            break;
          default:
            aValue = a[sortConfig.key] || '';
            bValue = b[sortConfig.key] || '';
        }
        
        // Get creation dates for secondary sorting
        const aCreationDate = a.creation_date ? new Date(a.creation_date) : new Date(0);
        const bCreationDate = b.creation_date ? new Date(b.creation_date) : new Date(0);
        
        // Handle string comparison with secondary sort by creation_date
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const result = aValue.localeCompare(bValue);
          
          // If primary values are equal, sort by creation_date
          if (result === 0) {
            const dateResult = aCreationDate.getTime() - bCreationDate.getTime();
            return sortConfig.direction === 'asc' ? dateResult : -dateResult;
          }
          
          return sortConfig.direction === 'asc' ? result : -result;
        }
        
        // Handle date comparison with secondary sort by creation_date
        if (aValue instanceof Date && bValue instanceof Date) {
          const result = aValue.getTime() - bValue.getTime();
          
          // If primary values are equal, sort by creation_date
          if (result === 0) {
            const dateResult = aCreationDate.getTime() - bCreationDate.getTime();
            return sortConfig.direction === 'asc' ? dateResult : -dateResult;
          }
          
          return sortConfig.direction === 'asc' ? result : -result;
        }
        
        // Handle number comparison with secondary sort by creation_date
        const result = aValue - bValue;
        
        // If primary values are equal, sort by creation_date
        if (result === 0) {
          const dateResult = aCreationDate.getTime() - bCreationDate.getTime();
          return sortConfig.direction === 'asc' ? dateResult : -dateResult;
        }
        
        return sortConfig.direction === 'asc' ? result : -result;
      });
    }
    
    return filteredList;
  }, [invoices, filterStatus, searchTerm, sortConfig]);
  
  // Get paginated invoices
  const paginatedInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedInvoices.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedInvoices, currentPage, pageSize]);
  
  // Total pages calculation
  const totalPages = useMemo(() => 
    Math.ceil(filteredAndSortedInvoices.length / pageSize), 
    [filteredAndSortedInvoices, pageSize]
  );
  
  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };
  
  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        // Toggle direction if same key
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        // New sort key, default to ascending
        return {
          key,
          direction: 'asc'
        };
      }
    });
  };
  
  // Handle pagination
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  
  // Handle status filter
  const handleStatusFilterChange = (status) => {
    setFilterStatus(status);
    setCurrentPage(1); // Reset to first page on filter change
    setFilterMenuAnchor(null); // Close filter menu
  };
  
  // Handle column visibility toggle
  const toggleColumnVisibility = (columnId) => {
    setColumns(
      columns.map(col => 
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  };
  
  // Handle invoice creation
  const handleCreateInvoice = () => {
    window.location.href = '/invoicing/facture';
  };
  
  // Handle invoice view/edit
  const handleEditInvoice = (invoiceId) => {
    navigate(`/invoicing/facture/${invoiceId}`);
  };
  
  // Handle invoice deletion
  const handleDeleteInvoice = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteConfirmOpen(true);
  };
  
  // Confirm invoice deletion
  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    
    try {
      const response = await sendRequest({
        apiPath: `invoicing/facture/${invoiceToDelete.id}`,
        type: 'DELETE'
      });
      
      if (response && response.success) {
        // Remove the deleted invoice from state
        setInvoices(prev => prev.filter(invoice => invoice.id !== invoiceToDelete.id));
        setDeleteConfirmOpen(false);
        setInvoiceToDelete(null);
      } else {
        console.error('Failed to delete invoice:', response);
        alert('Une erreur est survenue lors de la suppression de la facture.');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Une erreur est survenue lors de la suppression de la facture.');
    }
  };
  
  // Handle invoice export
  const handleExportInvoice = (invoice) => {
    // Implement export functionality
    console.log('Export invoice:', invoice);
  };
  
  // Handle outside clicks for column settings
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showColumnSettings && 
        columnSettingsRef.current && 
        !columnSettingsRef.current.contains(event.target) &&
        columnSettingsBtnRef.current &&
        !columnSettingsBtnRef.current.contains(event.target)
      ) {
        setShowColumnSettings(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnSettings]);
  
  // Set header actions
  useEffect(() => {
    setHeaderActions(
      <div className="flex gap-2">
        <button 
          ref={columnSettingsBtnRef}
          className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50"
          onClick={() => setShowColumnSettings(!showColumnSettings)}
        >
          <Settings size={16} />
          <span>Colonnes</span>
        </button>
        <button 
          className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50"
          onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
        >
          <Filter size={16} />
          <span>Filtres</span>
          <ChevronDown size={16} />
        </button>
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={() => setFilterMenuAnchor(null)}
        >
          {STATUS_OPTIONS.map((option) => (
            <MenuItem 
              key={option.value} 
              onClick={() => handleStatusFilterChange(option.value)}
              selected={filterStatus === option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
        <Button
          component={Link}
          to="/invoicing/facture"
          variant="contained"
          color="primary"
          startIcon={<Plus />}
        >
          Nouvelle Facture
        </Button>
      </div>
    );
    
    return () => setHeaderActions(null);
  }, [setHeaderActions, showColumnSettings, filterMenuAnchor, filterStatus]);
  
  return (
    <div className="max-w-full">
      {/* Column Settings Dropdown */}
      {showColumnSettings && (
        <div 
          ref={columnSettingsRef}
          className="absolute right-6 mt-12 w-64 bg-white rounded-lg shadow-lg z-10 border border-gray-200"
        >
          <div className="p-3 border-b border-gray-200">
            <h3 className="font-medium text-gray-700">Colonnes affichées</h3>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {columns.map(column => (
              <div 
                key={column.id} 
                className="flex items-center justify-between p-3 hover:bg-gray-50"
              >
                <span className="text-sm text-gray-700">{column.name}</span>
                <button 
                  onClick={() => column.id !== 'actions' && toggleColumnVisibility(column.id)}
                  className={`w-5 h-5 rounded flex items-center justify-center ${
                    column.visible ? 'bg-blue-500 text-white' : 'border border-gray-300'
                  } ${column.id === 'actions' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  disabled={column.id === 'actions'}
                >
                  {column.visible && <Check size={14} />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm mb-6 p-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="w-full md:w-64">
            <TextField
              placeholder="Rechercher une facture..."
              variant="outlined"
              fullWidth
              size="small"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} className="text-gray-400" />
                  </InputAdornment>
                ),
                style: { borderRadius: '8px' }
              }}
            />
          </div>
          
          <div className="flex-grow"></div>
          
          <div className="flex items-center text-sm text-gray-500">
            {filterStatus !== 'all' && (
              <div className="mr-2 flex items-center">
                <span className="mr-1">Filtre:</span>
                <Chip 
                  label={STATUS_OPTIONS.find(option => option.value === filterStatus)?.label} 
                  onDelete={() => handleStatusFilterChange('all')}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </div>
            )}
            
            <div>
              {filteredAndSortedInvoices.length} 
              {filteredAndSortedInvoices.length <= 1 ? ' facture trouvée' : ' factures trouvées'}
            </div>
          </div>
        </div>
      </div>
      
      {/* Invoices Table */}
      <Paper elevation={0} className="bg-white rounded-lg shadow-sm overflow-hidden">
        <TableContainer>
          <Table>
            <TableHeader
              columns={columns.filter(col => col.visible)}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
            <TableBody className="divide-y divide-gray-200">
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.filter(col => col.visible).length} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      Chargement des factures...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredAndSortedInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.filter(col => col.visible).length} align="center" sx={{ py: 5 }}>
                    <Typography variant="body1">
                      Aucune facture trouvée
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedInvoices.map((invoice, index) => (
                  <TableRow 
                    key={invoice.id}
                    hover={false}
                    className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} cursor-pointer transition-colors duration-150 ease-in-out hover:bg-blue-50`}
                    onClick={() => handleEditInvoice(invoice.id)}
                  >
                    {columns.find(col => col.id === 'number' && col.visible) && (
                      <TableCell className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">{invoice.num_document}</div>
                      </TableCell>
                    )}
                    
                    {columns.find(col => col.id === 'client' && col.visible) && (
                      <TableCell className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium mr-3">
                            {invoice.client?.entity_name ? (
                              invoice.client.entity_name.substring(0, 1).toUpperCase()
                            ) : (
                              (invoice.client?.firstname?.substring(0, 1) || '') + 
                              (invoice.client?.lastname?.substring(0, 1) || '')
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {getClientName(invoice.client)}
                            </div>
                            {invoice.client?.email && (
                              <div className="text-xs text-gray-500 max-w-xs truncate">
                                {invoice.client.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                    )}
                    
                    {columns.find(col => col.id === 'emission_date' && col.visible) && (
                      <TableCell className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Calendar size={16} className="text-blue-400 mr-2" />
                          {formatDate(invoice.date_emission)}
                        </div>
                      </TableCell>
                    )}
                    
                    {columns.find(col => col.id === 'due_date' && col.visible) && (
                      <TableCell className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Calendar size={16} className="text-blue-400 mr-2" />
                          {formatDate(invoice.date_echeance)}
                        </div>
                      </TableCell>
                    )}
                    
                    {columns.find(col => col.id === 'status' && col.visible) && (
                      <TableCell className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          invoice.status === 'Brouillon' ? 'bg-gray-100 text-gray-800' : 
                          invoice.status === 'Envoyée' ? 'bg-blue-100 text-blue-800' : 
                          invoice.status === 'Payée' ? 'bg-green-100 text-green-800' : 
                          invoice.status === 'Annulée' ? 'bg-red-100 text-red-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {invoice.status}
                        </span>
                      </TableCell>
                    )}
                    
                    {columns.find(col => col.id === 'amount' && col.visible) && (
                      <TableCell className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <div className="text-sm font-semibold text-gray-900 font-mono">
                            <span>{formatCurrency(invoice.total_tvac, invoice.currency)}</span>
                          </div>
                          <div className="text-xs text-gray-500 font-mono mt-1">
                            HTVA: {formatCurrency(invoice.total_htva, invoice.currency)}
                          </div>
                        </div>
                      </TableCell>
                    )}
                    
                    {columns.find(col => col.id === 'amount_htva' && col.visible) && (
                      <TableCell className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 font-mono">
                          {formatCurrency(invoice.total_htva, invoice.currency)}
                        </div>
                      </TableCell>
                    )}
                    
                    {columns.find(col => col.id === 'amount_tvac' && col.visible) && (
                      <TableCell className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 font-mono">
                          {formatCurrency(invoice.total_tvac, invoice.currency)}
                        </div>
                      </TableCell>
                    )}
                    
                    {columns.find(col => col.id === 'currency' && col.visible) && (
                      <TableCell className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          <span>{invoice.currency}</span>
                        </div>
                      </TableCell>
                    )}
                    
                    {columns.find(col => col.id === 'actions' && col.visible) && (
                      <TableCell className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3" onClick={(e) => e.stopPropagation()}>
                          <button className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50">
                            <Eye size={18} onClick={(e) => {
                              e.stopPropagation();
                              handleEditInvoice(invoice.id);
                            }} />
                          </button>
                          
                          <button className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50">
                            <Download size={18} onClick={(e) => {
                              e.stopPropagation();
                              handleExportInvoice(invoice);
                            }} />
                          </button>
                          
                          <button className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50">
                            <Trash2 size={18} onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteInvoice(invoice);
                            }} />
                          </button>
                          
                          <IconButton
                            component={Link}
                            to={`/invoicing/facture/${invoice.id}`}
                            title="Modifier la facture"
                          >
                            <EditIcon />
                          </IconButton>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Pagination */}
      {filteredAndSortedInvoices.length > 0 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={pageSize}
          totalItems={filteredAndSortedInvoices.length}
          pageSizeOptions={[5, 10, 25, 50]}
          onPageChange={goToPage}
          onPageSizeChange={setPageSize}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer la facture <strong>{invoiceToDelete?.num_document}</strong> ?
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
          <Button 
            onClick={confirmDeleteInvoice} 
            variant="contained" 
            color="error"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// Wrap with Dashboard layout
const FactureListPage = () => {
  return (
    <Dashboard 
      dashboardTitle="Mes Factures" 
      breadcrumbs={factureListBreadcrumbs}
    >
      <FactureListContent />
    </Dashboard>
  );
};

export default FactureListPage; 